import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "preact/jsx-runtime";
import { Building2, Users, ArrowLeft, Handshake, Home, BookOpen, PlugZap } from 'lucide-preact';
import { LAYOUT_CONSTANTS } from '../../constants/layout';
import { THEME_COLORS } from '../../constants/theme';
import { useTheme } from '../../hooks/useTheme';
import { useUserProfile, USER_ROLES } from '../../hooks/useUserProfile';
import { useLocation } from 'preact-iso';
export function SettingsNavigation({ activeSection, onSectionChange, onBackToApp, isOpen = true, onToggle }) {
    const { resolvedTheme } = useTheme();
    const { getCurrentCompanyRoleSet, hasCompanyRole, canManageCustomers } = useUserProfile();
    const location = useLocation();
    const assetsBaseUrl = import.meta.env.VITE_ASSETS_BASE_URL;
    // Get current company ID from URL
    const currentCompanyId = (() => {
        try {
            const match = location?.path?.match(/\/company\/([^/]+)/);
            return match?.[1] || null;
        }
        catch {
            return null;
        }
    })();
    const userRoles = getCurrentCompanyRoleSet();
    // Check if user is a company admin or has admin role in any workspace
    const canAccessUsers = () => {
        if (!userRoles || !currentCompanyId)
            return false;
        // Check if user is a company admin
        if (userRoles.IsAdmin || hasCompanyRole(USER_ROLES.ADMIN, currentCompanyId)) {
            return true;
        }
        // Check if user is admin in any workspace (using CanAdmin flag)
        return userRoles.WorkspaceRoles?.some(wsRole => wsRole.CanAdmin);
    };
    // Use different logos based on theme
    const logoUrl = resolvedTheme === 'dark'
        ? `${assetsBaseUrl}/assets/white-orbital-x.png`
        : `${assetsBaseUrl}/assets/black-orbital-x.png`;
    const companyNavigationItems = [
        { id: 'settings/company', label: 'Company', icon: Building2 },
    ];
    // Only allow Customers when user can manage customers AND current route company matches user's home company
    const routeCompanyIdMatch = (() => {
        try {
            const match = location?.path?.match(/\/company\/([^/]+)/);
            const routeCompanyId = match?.[1];
            return routeCompanyId && userRoles?.ClientCompanyId
                ? routeCompanyId === userRoles.ClientCompanyId
                : false;
        }
        catch {
            return false;
        }
    })();
    // Insert Customers below Company when allowed and company matches
    if (canManageCustomers && routeCompanyIdMatch) {
        companyNavigationItems.push({ id: 'settings/customers', label: 'Customers', icon: Handshake });
    }
    // Add Authentication if user can manage customers
    if (canManageCustomers) {
        companyNavigationItems.push({ id: 'settings/authentication', label: 'Authentication', icon: Building2 });
    }
    // Add Users if user has admin permissions (company or any workspace)
    if (canAccessUsers()) {
        companyNavigationItems.push({ id: 'settings/users', label: 'Users', icon: Users });
    }
    // Knowledge Base is always available in admin settings
    companyNavigationItems.push({ id: 'settings/knowledge-base', label: 'Knowledge Base', icon: BookOpen });
    // Integrations — company admins only
    if (userRoles?.IsAdmin) {
        companyNavigationItems.push({ id: 'settings/integrations', label: 'Integrations', icon: PlugZap });
    }
    // If user can manage customers AND is in a different company than home, add Back to Home Company at the top
    const addBackToHome = (() => {
        try {
            const match = location?.path?.match(/\/company\/([^/]+)/);
            const routeCompanyId = match?.[1];
            const homeCompanyId = userRoles?.ClientCompanyId;
            return Boolean(canManageCustomers && homeCompanyId && routeCompanyId && homeCompanyId !== routeCompanyId);
        }
        catch {
            return false;
        }
    })();
    const handleBackToHome = () => {
        const homeCompanyId = userRoles?.ClientCompanyId;
        if (homeCompanyId) {
            location.route(`/company/${homeCompanyId}`);
        }
    };
    return (_jsxs(_Fragment, { children: [isOpen && (_jsx("div", { className: "lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40", onClick: onToggle })), _jsxs("div", { className: `
        fixed inset-y-0 left-0 z-50
        w-64 
        bg-white dark:bg-slate-900 text-slate-900 dark:text-white border-r border-slate-200 dark:border-slate-700
        transform transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        flex flex-col h-screen
      `, children: [_jsx("div", { className: "p-4 flex items-center justify-center", style: { height: `${LAYOUT_CONSTANTS.TOP_NAV_HEIGHT}px` }, children: _jsxs("div", { className: "flex items-center", children: [_jsx("img", { src: logoUrl, alt: "OrbitalX", className: "h-[60px] w-auto", onError: (e) => {
                                        console.error('Failed to load logo from:', logoUrl);
                                        // Fallback to text if image fails to load
                                        e.currentTarget.style.display = 'none';
                                        e.currentTarget.nextElementSibling?.classList.remove('hidden');
                                    } }), _jsx("span", { className: "hidden text-xl font-bold", children: "OrbitalX" })] }) }), _jsxs("div", { className: "px-4 pb-4 space-y-2", children: [_jsxs("button", { onClick: () => {
                                    onBackToApp();
                                    // Close mobile menu when button is clicked
                                    if (onToggle && window.innerWidth < 1024) {
                                        onToggle();
                                    }
                                }, className: "w-full flex items-center px-3 py-2 text-left rounded-lg transition-colors duration-200 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white border border-slate-200 dark:border-slate-700", title: "Back to App", children: [_jsx(ArrowLeft, { className: "mr-3 w-4 h-4" }), _jsx("span", { className: "text-sm font-medium", children: "Back to App" })] }), addBackToHome && (_jsxs("button", { onClick: () => {
                                    handleBackToHome();
                                    if (onToggle && window.innerWidth < 1024) {
                                        onToggle();
                                    }
                                }, className: "w-full flex items-center px-3 py-2 text-left rounded-lg transition-colors duration-200 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white border border-slate-200 dark:border-slate-700", title: "Back to Home Company", children: [_jsx(Home, { className: "mr-3 w-4 h-4" }), _jsx("span", { className: "text-sm font-medium", children: "Back to Home Company" })] }))] }), _jsx("div", { className: "px-4 pb-2", children: _jsx("h2", { className: "text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider", children: "Admin" }) }), _jsx("nav", { className: "flex-1 overflow-y-auto px-4", children: _jsx("ul", { className: "space-y-2", children: companyNavigationItems.map((item) => (_jsx("li", { children: _jsxs("button", { onClick: () => {
                                        onSectionChange(item.id);
                                        // Close mobile menu when item is selected
                                        if (onToggle && window.innerWidth < 1024) {
                                            onToggle();
                                        }
                                    }, className: `
                    w-full flex items-center px-3 py-3 text-left rounded-lg transition-colors duration-200
                    ${activeSection === item.id
                                        ? 'text-slate-900 dark:text-slate-100 border'
                                        : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white'}
                  `, style: {
                                        backgroundColor: activeSection === item.id ? THEME_COLORS.BACKGROUNDS.LIGHT_SUBTLE : 'transparent',
                                        borderColor: activeSection === item.id ? THEME_COLORS.BORDERS.LIGHT : 'transparent'
                                    }, title: item.label, children: [_jsx(item.icon, { className: "mr-3 w-5 h-5 transition-colors duration-200", style: { color: activeSection === item.id ? THEME_COLORS.GREEN[500] : 'inherit' } }), _jsx("span", { className: "text-sm font-medium transition-colors duration-200", style: { color: activeSection === item.id ? THEME_COLORS.GREEN[600] : 'inherit' }, children: item.label })] }) }, item.id))) }) }), _jsx("div", { className: "flex-shrink-0 p-2 border-t border-slate-200 dark:border-slate-700", children: _jsxs("div", { className: "text-xs text-slate-400 dark:text-slate-400 text-center", children: ["v", import.meta.env.VITE_APP_VERSION || '1.0'] }) })] })] }));
}
